<!-- The group is re-bound here on purpose: this row must own its binding, not inherit one. -->
<!--
  The reorder column and the remove action line up with the FIRST row of fields, not with the middle
  of the block: a Visit website button is three field rows tall, and a handle floating at its centre
  reads as belonging to the URL rather than to the button.

  The alignment is done with an empty `.form-label` spacer instead of a fixed margin, so it follows
  the real label height — change the font or the label spacing and it still lines up.
-->
<div class="d-flex align-items-start gap-2 position-relative" [formGroup]="group">

  <!-- The drag handle sits in the card's top-left corner, out of the flow: it grabs the whole row,
       so it belongs to the card rather than to any field line. Pointer-only by nature — the two
       arrows below are the keyboard path (RNF-07). -->
  @if (!locked) {
  <i cdkDragHandle class="ri-drag-move-2-line text-muted position-absolute top-0 start-0"
    aria-hidden="true"></i>

  <div class="d-flex flex-column align-items-center">
    <span class="form-label mb-2" aria-hidden="true">&nbsp;</span>
    <button type="button" class="btn btn-sm btn-link p-0 lh-1" [disabled]="!canMove(-1)" (click)="move(-1)"
      [attr.aria-label]="'channel.templateBuilder.buttonMoveUp' | translate">
      <i class="ri-arrow-up-s-line"></i>
    </button>
    <button type="button" class="btn btn-sm btn-link p-0 lh-1" [disabled]="!canMove(1)" (click)="move(1)"
      [attr.aria-label]="'channel.templateBuilder.buttonMoveDown' | translate">
      <i class="ri-arrow-down-s-line"></i>
    </button>
  </div>
  }

  <div class="flex-grow-1">
    <div class="row g-2">

      <!-- RF-50 — the type is editable in place. Not bound to the form: the engine is the only
           writer of `type`, so the previous value stays readable and a refusal can be put back. -->
      <div class="col-12" [ngClass]="typeColumnClass">
        <label class="form-label" [for]="'button-type-' + index">
          {{ 'channel.templateBuilder.buttonActionType' | translate }}
        </label>
        @if (locked) {
        <!-- The order details format accepts this one type and no other, so there is nothing to
             choose: the type is shown, not selected. -->
        <input [id]="'button-type-' + index" type="text" class="form-control" readonly
          [value]="typeLabel | translate" />
        } @else {
        <mat-select #typeSelect="matSelect" [id]="'button-type-' + index" class="form-control" [value]="type"
          (selectionChange)="changeType($event.value, typeSelect)">
          @for (option of buttonTypes; track option.value) {
          <mat-option [value]="option.value">{{ option.label | translate }}</mat-option>
          }
        </mat-select>
        }
      </div>

      <!-- RF-38 — Custom sub-type. Sits between the action type and the label, as on Meta's own
           screen, and keeps a Custom button on a single row. -->
      @if (type === 'QUICK_REPLY') {
      <div class="col-12 col-md-4">
        <label class="form-label" [for]="'button-subtype-' + index">
          {{ 'channel.templateBuilder.buttonSubType' | translate }}
        </label>
        <mat-select [id]="'button-subtype-' + index" class="form-control" formControlName="subType">
          @for (subType of customSubTypes; track subType.value) {
          <mat-option [value]="subType.value">{{ subType.label | translate }}</mat-option>
          }
        </mat-select>
      </div>
      }

      <!-- RF-37, RF-44 — every type carries a label; Copy offer code fixes it. Type and label share
           the first row at half width each: a third was not enough for "Ligar no WhatsApp", and the
           type-specific fields read better on their own row below. -->
      <div class="col-12" [ngClass]="labelColumnClass">
        <label class="form-label" [for]="'button-text-' + index">
          {{ 'channel.templateBuilder.buttonLabel' | translate }}
        </label>
        <input [id]="'button-text-' + index" type="text" class="form-control" formControlName="text"
          [maxlength]="limits.BUTTON_TEXT_MAX_LENGTH" [readonly]="hasFixedLabel" />
        @if (!hasFixedLabel) {
        <div class="d-flex justify-content-between gap-2">
          @if (isMissing('text')) {
          <span class="form-text small text-danger" role="alert">
            {{ 'channel.templateBuilder.fieldRequired' | translate }}
          </span>
          } @else {
          <span></span>
          }
          <span class="form-text small text-nowrap">
            {{ value('text').length }}/{{ limits.BUTTON_TEXT_MAX_LENGTH }}
          </span>
        </div>
        }
      </div>

      <!-- RF-39, RF-40, RF-41 — Visit website. -->
      @if (type === 'URL') {
      <div class="col-12 col-md-4">
        <label class="form-label" [for]="'button-urltype-' + index">
          {{ 'channel.templateBuilder.buttonUrlType' | translate }}
        </label>
        <mat-select [id]="'button-urltype-' + index" class="form-control" formControlName="urlType">
          @for (urlType of urlTypes; track urlType.value) {
          <mat-option [value]="urlType.value">{{ urlType.label | translate }}</mat-option>
          }
        </mat-select>
      </div>
      <div class="col-12 col-md-8">
        <label class="form-label" [for]="'button-url-' + index">
          {{ 'channel.templateBuilder.buttonUrlLabel' | translate }}
        </label>
        <div class="input-group">
          <input [id]="'button-url-' + index" type="url" class="form-control" formControlName="url"
            [maxlength]="limits.BUTTON_URL_MAX_LENGTH" placeholder="https://" />
          <!-- The marker is shown outside the editable text and appended only on submit (RF-40). -->
          @if (value('urlType') === 'DYNAMIC') {
          <span class="input-group-text">&#123;&#123;1&#125;&#125;</span>
          }
        </div>
        @if (isMissing('url')) {
        <span class="form-text small text-danger" role="alert">
          {{ 'channel.templateBuilder.fieldRequired' | translate }}
        </span>
        }
        @if (value('urlType') === 'DYNAMIC') {
        <span class="form-text small">{{ 'channel.templateBuilder.buttonUrlDynamicNote' | translate }}</span>
        }
      </div>
      <div class="col-12">
        <div class="form-check">
          <!-- RF-41 — shown, explained, never selectable. -->
          <input class="form-check-input" type="checkbox" disabled [id]="'button-tracking-' + index" />
          <label class="form-check-label text-muted" [for]="'button-tracking-' + index">
            {{ 'channel.templateBuilder.buttonAppTracking' | translate }}
            <i class="ri-information-line" [matTooltip]="'channel.templateBuilder.buttonAppTrackingHelp' | translate"></i>
          </label>
        </div>
      </div>
      }

      <!-- RF-42 — Call on WhatsApp. -->
      @if (type === 'VOICE_CALL') {
      <div class="col-12">
        <div class="alert alert-info py-2 mb-0 font-13" role="note">
          {{ 'channel.templateBuilder.buttonVoiceCallNote' | translate }}
          <a [href]="voiceCallDocumentationUrl" target="_blank" rel="noopener">
            {{ 'channel.templateBuilder.buttonVoiceCallLink' | translate }}
          </a>
        </div>
      </div>
      }

      <!-- RF-43 — Call. -->
      @if (type === 'PHONE_NUMBER') {
      <div class="col-12 col-md-4">
        <label class="form-label" [for]="'button-country-' + index">
          {{ 'channel.templateBuilder.buttonCountry' | translate }}
        </label>
        <mat-select [id]="'button-country-' + index" class="form-control" formControlName="countryCode">
          @for (country of countries; track country.name) {
          <mat-option [value]="country.code">{{ country.flag }} {{ country.name }} ({{ country.code }})</mat-option>
          }
        </mat-select>
      </div>
      <div class="col-12 col-md-8">
        <label class="form-label" [for]="'button-phone-' + index">
          {{ 'channel.templateBuilder.buttonPhone' | translate }}
        </label>
        <input [id]="'button-phone-' + index" type="tel" class="form-control" formControlName="phoneNumber"
          [maxlength]="limits.BUTTON_PHONE_NUMBER_MAX_LENGTH" />
        <div class="d-flex justify-content-end">
          <span class="form-text small text-nowrap">
            {{ value('phoneNumber').length }}/{{ limits.BUTTON_PHONE_NUMBER_MAX_LENGTH }}
          </span>
        </div>
      </div>
      }

      <!-- DEV-1992 — the Pix code is not part of the template: it travels as this button's action
           parameter at send time, which is what takes it out of the body character budget. -->
      @if (type === 'ORDER_DETAILS') {
      <div class="col-12">
        <div class="alert alert-info py-2 mb-0 font-13" role="note">
          {{ 'channel.templateBuilder.buttonOrderDetailsNote' | translate }}
        </div>
      </div>
      }

      <!-- RF-44 — Copy offer code. -->
      @if (type === 'COPY_CODE') {
      <div class="col-12 col-md-6">
        <label class="form-label" [for]="'button-code-' + index">
          {{ 'channel.templateBuilder.buttonOfferCode' | translate }}
        </label>
        <input [id]="'button-code-' + index" type="text" class="form-control" formControlName="offerCode"
          [maxlength]="limits.BUTTON_COPY_CODE_MAX_LENGTH" />
        <div class="d-flex justify-content-between gap-2">
          @if (isMissing('offerCode')) {
          <span class="form-text small text-danger" role="alert">
            {{ 'channel.templateBuilder.fieldRequired' | translate }}
          </span>
          } @else {
          <span></span>
          }
          <span class="form-text small text-nowrap">
            {{ value('offerCode').length }}/{{ limits.BUTTON_COPY_CODE_MAX_LENGTH }}
          </span>
        </div>
      </div>
      }
    </div>
  </div>

  @if (!locked) {
  <div class="d-flex flex-column align-items-center">
    <span class="form-label mb-2" aria-hidden="true">&nbsp;</span>
    <button type="button" class="btn btn-sm btn-soft-danger" (click)="remove()"
      [attr.aria-label]="'channel.templateBuilder.buttonRemove' | translate">
      <i class="ri-delete-bin-line"></i>
    </button>
  </div>
  }
</div>
